import { getEventPropsBuilder, toSnakeCase } from '@/tracking/amplitude/utils';
import type { RewardsHubSharedState, RewardsHubTrackSendEvent, PromoBaseContext } from './rewards-hub';
import {
    trackRewardsHubAmplitude,
    mapRewardsHubSharedStateToAmplitude,
    mapPromoBaseContextToAmplitudeProps
} from './rewards-hub';
import { REWARDS_HUB_AMP_EVENT, REWARDS_MODULE, REWARDS_SOURCE, REWARDS_PAGE_NAME } from './event-names';

export type PromoOfferCtaClickedContext = PromoBaseContext & {
    optedIn?: string;
};

const mapOptInState = (optInState: string): string => (optInState === 'ONGOING' ? 'opted_in' : toSnakeCase(optInState));

export const trackPromoOfferPageViewedToAmplitude = (args: {
    sendEvent: RewardsHubTrackSendEvent | undefined;
    sharedState?: RewardsHubSharedState | null;
    context?: PromoBaseContext;
    source?: string;
}) => {
    const { sendEvent, sharedState, context, source } = args;

    if (!sendEvent) {
        return;
    }

    const eventProps = getEventPropsBuilder((ctx, src) => ({
        event: REWARDS_HUB_AMP_EVENT.PROMO_OFFER_PAGE_VIEWED,
        'Page Name': REWARDS_PAGE_NAME,
        Source: src ?? REWARDS_SOURCE.DIRECT,
        ...mapRewardsHubSharedStateToAmplitude(sharedState),
        ...mapPromoBaseContextToAmplitudeProps(ctx)
    }))(context, source);

    sendEvent([
        {
            destination: 'amplitude',
            eventProps
        }
    ]);
};

export const trackPromoOfferTermsExpandedToAmplitude = (args: {
    sendEvent: RewardsHubTrackSendEvent | undefined;
    sharedState?: RewardsHubSharedState | null;
    context?: PromoBaseContext;
}) => {
    const { sendEvent, sharedState, context } = args;

    trackRewardsHubAmplitude({
        sendEvent,
        event: REWARDS_HUB_AMP_EVENT.PROMO_OFFER_TERMS_EXPANDED,
        sharedState,
        props: {
            Module: REWARDS_MODULE.PROMO_HUB_OFFER,
            Source: REWARDS_SOURCE.DIRECT,
            ...mapPromoBaseContextToAmplitudeProps(context)
        }
    });
};

export const trackPromoOfferCtaClickedToAmplitude = (args: {
    sendEvent: RewardsHubTrackSendEvent | undefined;
    sharedState?: RewardsHubSharedState | null;
    linkText: string;
    linkUrl?: string;
    context?: PromoOfferCtaClickedContext;
}) => {
    const { sendEvent, sharedState, linkText, context } = args;

    trackRewardsHubAmplitude({
        sendEvent,
        event: REWARDS_HUB_AMP_EVENT.PROMO_OFFER_CTA_CLICKED,
        sharedState,
        props: {
            Module: REWARDS_MODULE.PROMO_HUB_OFFER,
            'Link Text': toSnakeCase(linkText),
            ...mapPromoBaseContextToAmplitudeProps(context),
            ...(context?.optedIn ? { 'Opted In': mapOptInState(context.optedIn) } : {}),
            Source: REWARDS_SOURCE.DIRECT
        }
    });
};
